import type {
  ApiCxBranch, ApiCxConversation, ApiCxCustomer, ApiCxInboxFilter,
} from '../api/types';
import type { CxView } from '../global/types';
import type { LangFn } from './localization';

import { TELEGRAF_PAY_URL } from '../config';

// Deep links that work across clients: the app intercepts them, other clients open the web app
const CX_LINK_BASE = 'https://telegraf.chat/cx';
const CX_LINK_REGEX = /^https?:\/\/telegraf\.chat\/cx\/(conversation|customer)\/(\d+)/;

// Mirrors `domain.EncodeEmailPhone` on the server: email-as-identity accounts
// travel through the phone field of the MTProto auth flow
const EMAIL_PHONE_PREFIX = '888';
const EMAIL_PHONE_ESCAPE = 'q';
const EMAIL_PHONE_ESCAPES: Record<string, string> = {
  '@': '0',
  '.': '1',
  '-': '2',
  _: '3',
  '+': '4',
  [EMAIL_PHONE_ESCAPE]: '5',
};
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string) {
  return EMAIL_REGEX.test(email.trim());
}

export function encodeEmailPhone(email: string) {
  const normalized = email.trim().toLowerCase();
  let result = EMAIL_PHONE_PREFIX;

  for (const char of normalized) {
    const escaped = EMAIL_PHONE_ESCAPES[char];
    if (escaped) {
      result += EMAIL_PHONE_ESCAPE + escaped;
    } else if (/[a-z0-9]/.test(char)) {
      result += char;
    } else {
      return undefined;
    }
  }

  return result;
}

export function decodeEmailPhone(phone?: string) {
  if (!phone?.startsWith(EMAIL_PHONE_PREFIX) || !/[a-z]/.test(phone)) return undefined;

  const reverse = Object.fromEntries(Object.entries(EMAIL_PHONE_ESCAPES).map(([key, value]) => [value, key]));
  let result = '';
  const body = phone.slice(EMAIL_PHONE_PREFIX.length);
  for (let i = 0; i < body.length; i++) {
    if (body[i] === EMAIL_PHONE_ESCAPE) {
      result += reverse[body[i + 1]] || '';
      i++;
    } else {
      result += body[i];
    }
  }

  return result;
}

export function buildCxConversationLink(conversationId: string) {
  return `${CX_LINK_BASE}/conversation/${conversationId}`;
}

// The checkout page takes the pay token in the address fragment, which browsers never send to a server
export function buildCxCheckoutUrl(workspaceId: string, payToken: string) {
  return `${TELEGRAF_PAY_URL}/checkout/?workspace=${workspaceId}#token=${encodeURIComponent(payToken)}`;
}

export function parseCxLink(url: string) {
  const match = url.match(CX_LINK_REGEX);
  if (!match) return undefined;

  return { type: match[1] as 'conversation' | 'customer', id: match[2] };
}

const CX_HASH_PREFIX = 'cx/';
const CX_ID_REGEX = /^\d+$/;
const CX_INBOX_FILTERS = new Set<ApiCxInboxFilter>(['mine', 'unassigned', 'open', 'snoozed', 'done', 'all']);

// Location hash of a CX screen, the counterpart of a chat's `#<chatId>`: `cx/inbox/mine/42`, `cx/customer/7`...
export function buildCxViewHash(view: CxView) {
  switch (view.type) {
    case 'inbox':
      return [CX_HASH_PREFIX + view.type, view.filter, view.conversationId].filter(Boolean).join('/');
    case 'customer':
      return [CX_HASH_PREFIX + view.type, view.customerId, view.conversationId].filter(Boolean).join('/');
    case 'company':
      return `${CX_HASH_PREFIX}${view.type}/${view.companyId}`;
    case 'activity':
      return [CX_HASH_PREFIX + view.type, view.conversationId].filter(Boolean).join('/');
    default:
      return CX_HASH_PREFIX + view.type;
  }
}

export function parseCxViewHash(hash: string): CxView | undefined {
  const path = hash.replace(/^#/, '');
  if (!path.startsWith(CX_HASH_PREFIX)) return undefined;

  const [type, first, second] = path.slice(CX_HASH_PREFIX.length).split('/');
  switch (type) {
    case 'inbox': {
      const filter = first as ApiCxInboxFilter;
      if (!CX_INBOX_FILTERS.has(filter)) return { type, filter: 'unassigned' };
      return { type, filter, conversationId: isId(second) ? second : undefined };
    }
    case 'customer':
      return isId(first) ? { type, customerId: first, conversationId: isId(second) ? second : undefined } : undefined;
    case 'company':
      return isId(first) ? { type, companyId: first } : undefined;
    case 'activity':
      return { type, conversationId: isId(first) ? first : undefined };
    case 'customers':
    case 'companies':
    case 'members':
    case 'settings':
      return { type };
    default:
      return undefined;
  }
}

function isId(value?: string) {
  return Boolean(value && CX_ID_REGEX.test(value));
}

// Where browser Back leads from a CX screen: an opened item returns to its list
export function getCxParentView(view: CxView): CxView | undefined {
  switch (view.type) {
    case 'inbox':
      return view.conversationId ? { type: 'inbox', filter: view.filter } : undefined;
    case 'customer':
      return { type: 'customers' };
    case 'company':
      return { type: 'companies' };
    case 'activity':
      return view.conversationId ? { type: 'activity' } : undefined;
    default:
      return undefined;
  }
}

export function matchesCxInboxFilter(
  conversation: ApiCxConversation, filter: ApiCxInboxFilter, currentUserId?: string,
) {
  switch (filter) {
    case 'mine':
      return conversation.status === 'open' && conversation.assigneeId === currentUserId;
    case 'unassigned':
      return conversation.status === 'open' && !conversation.assigneeId;
    case 'open':
      return conversation.status === 'open';
    case 'snoozed':
      return conversation.status === 'snoozed';
    case 'done':
      return conversation.status === 'done';
    default:
      return true;
  }
}

export type CxEvent = {
  type: string;
  arg?: string;
  extra?: string;
};

// Timeline events are stored as `type:arg:extra`, e.g. `assign:42` or `discussion:<chat>:<message>`
export function parseCxEvent(raw?: string): CxEvent | undefined {
  if (!raw) return undefined;

  const [type, arg, extra] = raw.split(':');
  return { type, arg, extra };
}

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = 60 * MINUTE_SECONDS;
const DAY_SECONDS = 24 * HOUR_SECONDS;

// Compact age like Kustomer's tabs: "now", "19m", "3h", "2d"
export function formatCxAge(lang: LangFn, timestamp: number) {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - timestamp);
  if (seconds < MINUTE_SECONDS) return lang('CxAgeNow');
  if (seconds < HOUR_SECONDS) return lang('CxAgeMinutes', { count: Math.floor(seconds / MINUTE_SECONDS) });
  if (seconds < DAY_SECONDS) return lang('CxAgeHours', { count: Math.floor(seconds / HOUR_SECONDS) });

  return lang('CxAgeDays', { count: Math.floor(seconds / DAY_SECONDS) });
}

// Slack's thread summary wording: "Last reply 7 days ago"
export function formatCxLastReply(lang: LangFn, timestamp: number) {
  const seconds = Math.max(0, Math.floor(Date.now() / 1000) - timestamp);
  if (seconds < MINUTE_SECONDS) return lang('CxSubLastReplyNow');
  if (seconds < HOUR_SECONDS) {
    const count = Math.floor(seconds / MINUTE_SECONDS);
    return lang('CxSubLastReplyMinutes', { count }, { pluralValue: count });
  }
  if (seconds < DAY_SECONDS) {
    const count = Math.floor(seconds / HOUR_SECONDS);
    return lang('CxSubLastReplyHours', { count }, { pluralValue: count });
  }

  const count = Math.floor(seconds / DAY_SECONDS);
  return lang('CxSubLastReplyDays', { count }, { pluralValue: count });
}

// WhatsApp recipients are stored as bare digits; show them as international numbers
export function formatCxBranchRecipient(branch: ApiCxBranch) {
  return branch.channel === 'whatsapp' ? `+${branch.recipient}` : branch.recipient;
}

export function getCxCustomerInitials(customer?: ApiCxCustomer) {
  const name = customer?.name.trim() || '?';
  const parts = name.split(/\s+/);
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

const INVITE_HASH_REGEX = /^[a-z0-9]{8,32}$/;

// Accepts a bare invite code or any link containing `join=<code>` / `/join/<code>`
export function parseCxInviteHash(input: string) {
  const value = input.trim();
  const match = value.match(/join[=/]([a-z0-9]+)/i);
  const hash = (match ? match[1] : value).toLowerCase();

  return INVITE_HASH_REGEX.test(hash) ? hash : undefined;
}

export function buildCxInviteLink(inviteHash: string) {
  return `${window.location.origin}${window.location.pathname}#join=${inviteHash}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Short inbox-style timestamp: time today, weekday this week, date otherwise
export function formatCxShortTime(langCode: string, timestamp: number) {
  const date = new Date(timestamp * 1000);
  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(langCode, { hour: '2-digit', minute: '2-digit' });
  }
  if (now.getTime() - date.getTime() < 6 * DAY_MS) {
    return date.toLocaleDateString(langCode, { weekday: 'short' });
  }

  return date.toLocaleDateString(langCode, { month: 'short', day: 'numeric' });
}

export function formatCxTime(langCode: string, timestamp: number) {
  return new Date(timestamp * 1000).toLocaleTimeString(langCode, { hour: '2-digit', minute: '2-digit' });
}

export function formatCxDate(langCode: string, timestamp: number) {
  return new Date(timestamp * 1000).toLocaleDateString(langCode, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export function formatCxDateTime(langCode: string, timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString(langCode, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export const CX_CHANNEL_LANG_KEYS = {
  widget: 'CxChannelWidget',
  line: 'CxChannelLine',
  whatsapp: 'CxChannelWhatsApp',
  instagram: 'CxChannelInstagram',
  messenger: 'CxChannelMessenger',
  email: 'CxChannelEmail',
} as const;

export const CX_ROLE_LANG_KEYS = {
  owner: 'CxRoleOwner',
  admin: 'CxRoleAdmin',
  agent: 'CxRoleAgent',
  member: 'CxRoleMember',
} as const;

const JOIN_HASH_PREFIX = '#join=';
const PENDING_JOIN_STORAGE_KEY = 'tt-cx-pending-join';

// Invite links (`#join=<code>`) must survive sign-in, which resets the location hash
export function captureCxJoinHash() {
  if (!window.location.hash.startsWith(JOIN_HASH_PREFIX)) return;

  const inviteHash = parseCxInviteHash(window.location.hash);
  window.history.replaceState(window.history.state, '', window.location.pathname);
  if (!inviteHash) return;

  try {
    sessionStorage.setItem(PENDING_JOIN_STORAGE_KEY, inviteHash);
  } catch (err) {
    // Storage may be unavailable in private mode; the user can still paste the code
  }
}

export function consumeCxPendingJoinHash() {
  try {
    const inviteHash = sessionStorage.getItem(PENDING_JOIN_STORAGE_KEY) || undefined;
    sessionStorage.removeItem(PENDING_JOIN_STORAGE_KEY);
    return inviteHash;
  } catch (err) {
    return undefined;
  }
}
